import { InAppState } from "../base/InAppState";
import { Grid, html } from "gridjs";

// Registers the <accommodation-form> and <accommodation-detail> custom elements
// used by the template.
import "../../components/accommodation/form";
import "../../components/accommodation/detail";

import template from "./AccommodationListTemplate.html";
import { _lcs } from "../../i18n";
import { gridLanguage } from "../../core/grid";
import { purgeLines } from "../../core/PurgeSummary";

// The three maps below hold German source strings, which are also their
// dictionary keys: module constants are evaluated before I18n exists, so the
// translation happens where they are read.

// Accommodation status -> display label (two-value enum: draft | published).
const STATUS_LABELS = {
    draft     : "Entwurf",
    published : "Veröffentlicht"
};

// Accommodation type -> display label.
const TYPE_LABELS = {
    hotel      : "Hotel",
    airbnb     : "Airbnb",
    apartment  : "Apartment",
    guesthouse : "Gästehaus",
    bnb        : "B&B",
    hostel     : "Hostel",
    camping    : "Camping",
    other      : "Sonstige"
};

// Region key -> display label (platform-defined regions).
const REGION_LABELS = {
    saechsische_schweiz : "Sächsische Schweiz"
};

/**
 * AccommodationList — lists all accommodations in the system in a dynamic
 * (searchable/sortable/paginated) GridJS table. Data comes from GET
 * /accommodation; owner names are resolved from GET /admin/user. Each row has
 * "Ghost" (confirm-modal → POST /admin/ghostsession → opens the accommodation's
 * client-admin signed in as its owner), "Detail" (opens <accommodation-detail>)
 * and "Bearbeiten" (opens <accommodation-form> in edit mode) actions, and
 * "Löschen" — the hard deletion with everything the house holds, its owner
 * too when it was their last house (core-api PurgeService), each step behind
 * a confirmation.
 */
export class AccommodationList extends InAppState
{
    static ROUTE = '/client/accommodation';
    static ID = 'accommodation-list';

    async enter()
    {
        this.app.view.render( this.app.view.pageContent, template );

        const root = this.app.view.pageContent;
        this.stateEl = root.querySelector( '[data-id="accommodations-state"]' );
        this.accForm = root.querySelector( '[data-id="acc-form"]' );
        this.accDetail = root.querySelector( '[data-id="acc-detail"]' );

        this.accommodations = [];
        this.accById = new Map();
        this.clientUsers = [];

        // Tracked listeners — InfrontJS removes them automatically on state exit.
        this.addEventListener(
            root.querySelector( '[data-id="btn-new-accommodation"]' ),
            "click",
            () => this.accForm.openCreate( this._existingSlugs(), this.clientUsers )
        );
        this.addEventListener( this.accForm, "accommodation-saved", () => this.loadAccommodations() );

        // Row actions — delegated so it survives GridJS re-renders (sort/search/page).
        this.addEventListener( this.stateEl, "click", ( e ) => this.onGridAction( e ) );

        await this.loadAccommodations();
    }

    onGridAction( e )
    {
        const btn = e.target.closest( "[data-action]" );
        if ( !btn || !this.stateEl.contains( btn ) ) return;

        const accommodation = this.accById.get( btn.getAttribute( "data-accommodation-id" ) );
        if ( !accommodation ) return;

        switch ( btn.getAttribute( "data-action" ) )
        {
            case "detail": this.accDetail.open( accommodation, this._ownerLabel( accommodation ) ); break;
            // Owner is not editable — the form shows it read-only (disabled) in edit mode.
            case "edit":   this.accForm.openEdit( accommodation, this._existingSlugs(), this._ownerLabel( accommodation ) ); break;
            case "ghost":  this.openGhostSession( accommodation ); break;
            case "delete": this.onDelete( accommodation ); break;
        }
    }

    /* -------------------------------- Delete -------------------------------- */

    /**
     * Hard-delete an accommodation. First what it takes with it (GET
     * /admin/accommodation/:id/purge) in a confirmation; when the owner goes
     * too (their last house), a second confirmation for that. Then DELETE
     * /admin/accommodation/:id. Nobody is mailed.
     */
    async onDelete( accommodation )
    {
        const name = accommodation.name || accommodation.slug;

        let preview;
        try
        {
            const { result } = await this.app.api.get( "/admin/accommodation/" + accommodation.id + "/purge" );
            if ( !result || +result.status !== 200 || !result.data )
            {
                throw new Error( "Unexpected purge preview." );
            }
            preview = result.data;
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.toast( _lcs( "Löschen ist gerade nicht möglich. Bitte versuche es später erneut." ), "error" );
            return;
        }

        const lines = purgeLines( preview );
        const ok = await this.app.view.confirm(
            _lcs( "Unterkunft endgültig löschen?" ),
            _lcs( "„{name}“ wird mit allen zugehörigen Daten unwiderruflich gelöscht — auch die Gäste-App unter {slug} ist danach nicht mehr erreichbar.", { name, slug : accommodation.slug } ) +
                ( lines.length ? "\n\n" + _lcs( "Dabei werden gelöscht:" ) + "\n" + lines.join( "\n" ) : "" ),
            _lcs( "Endgültig löschen" ),
            _lcs( "Abbrechen" ),
            { danger : true }
        );
        if ( !ok ) return;

        const owner = preview.owner;
        if ( owner && ( owner.deletesAccount || owner.removesClientRole ) )
        {
            const ownerName = owner.name || owner.email || this._ownerLabel( accommodation );
            const confirmed = await this.app.view.confirm(
                owner.deletesAccount ? _lcs( "Gastgeber ebenfalls löschen?" ) : _lcs( "Client-Rolle entfernen?" ),
                owner.deletesAccount
                    ? _lcs( "„{name}“ ist die letzte Unterkunft von {owner}. Das Gastgeber-Konto wird deshalb ebenfalls gelöscht — samt Anmeldungen, und die Organisation, sobald sie leer ist.", { name, owner : ownerName } )
                    : _lcs( "„{name}“ ist die letzte Unterkunft von {owner}. Das Konto hat weitere Rollen und bleibt bestehen; es verliert nur die Client-Rolle.", { name, owner : ownerName } ),
                owner.deletesAccount ? _lcs( "Unterkunft und Gastgeber löschen" ) : _lcs( "Löschen und Rolle entfernen" ),
                _lcs( "Abbrechen" ),
                { danger : true }
            );
            if ( !confirmed ) return;
        }

        try
        {
            const { result } = await this.app.api.delete( "/admin/accommodation/" + accommodation.id );
            if ( !result || +result.status !== 200 )
            {
                throw new Error( "Unexpected delete response." );
            }
            const removedOwner = result.data && Array.isArray( result.data.userIds ) && owner && result.data.userIds.includes( owner.id );
            this.app.view.toast( removedOwner
                ? _lcs( "Unterkunft „{name}“ und ihr Gastgeber wurden gelöscht.", { name } )
                : _lcs( "Unterkunft „{name}“ wurde gelöscht.", { name } ), "success" );
            await this.loadAccommodations();
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.toast( +e?.status === 404
                ? _lcs( "Unterkunft wurde nicht gefunden." )
                : _lcs( "Löschen fehlgeschlagen. Bitte versuche es später erneut." ), "error" );
        }
    }

    /* ---------------------------- Ghost session ----------------------------- */

    /**
     * Confirm, then create a short-lived ghost-session action token for the
     * accommodation's owner and open its client-admin at #/token/{token} —
     * where TokenState exchanges it for a session (automatic sign-in).
     */
    async openGhostSession( accommodation )
    {
        const confirmed = await this.app.view.confirm(
            _lcs( "Ghost-Session starten?" ),
            _lcs( 'Du wirst im Client-Admin von "{name}" automatisch als dessen Eigentümer angemeldet.', {
                name : accommodation.name || accommodation.slug
            } ),
            _lcs( "Ghost-Session starten" ),
            _lcs( "Abbrechen" )
        );
        if ( !confirmed ) return;

        try
        {
            const { result } = await this.app.api.post(
                "/admin/ghostsession",
                { "accommodationId": String( accommodation.id ) }
            );

            if ( !result || +result.status !== 201 || !result.data || !result.data.token )
            {
                throw new Error( "Unexpected ghost session response." );
            }

            const url = this._clientAdminUrl( result.data.slug || accommodation.slug ) +
                "#/token/" + encodeURIComponent( result.data.token );
            window.open( url, "_blank", "noopener" );
        }
        catch ( e )
        {
            console.error( e );
            this.app.view.toast( _lcs( "Ghost-Session konnte nicht gestartet werden." ), "error" );
        }
    }

    // Client-admin base URL for a slug, from config "clientAdminUrl" — a
    // template with a {slug} placeholder (e.g. "http://{slug}.localhost:3001/admin/").
    // With the trailing slash: "/admin" answers a 301, and on a phone that
    // knows the guest-app the redirect would land in its service worker.
    _clientAdminUrl( slug )
    {
        const tpl = this.app.config.get( "clientAdminUrl", "http://{slug}.localhost:3001/admin/" );
        return tpl.replace( "{slug}", encodeURIComponent( slug ) );
    }

    // Lowercased slugs of all loaded accommodations — used for client-side
    // uniqueness checks in the create/edit form.
    _existingSlugs()
    {
        return ( this.accommodations || [] ).map( ( a ) => String( a.slug || "" ).toLowerCase() );
    }

    /* ------------------------------- Loading -------------------------------- */

    async loadAccommodations()
    {
        this.showMessage( "building-2", _lcs( "Lade Unterkünfte …" ), _lcs( "Einen Moment bitte." ) );

        let accommodations = [];
        let usersById = new Map();
        let clientUsers = [];
        try
        {
            // Accommodations are required; the user list is best-effort (used only
            // to show a friendly owner name instead of a raw id).
            const [ accRes, userRes ] = await Promise.all( [
                this.app.api.get( "/accommodation" ),
                this.app.api.get( "/admin/user" ).catch( () => null )
            ] );

            if ( !accRes || !accRes.result || +accRes.result.status !== 200 || !Array.isArray( accRes.result.data ) )
            {
                throw new Error( "Unexpected /accommodation response." );
            }
            accommodations = accRes.result.data;

            if ( userRes && userRes.result && +userRes.result.status === 200 && Array.isArray( userRes.result.data ) )
            {
                userRes.result.data.forEach( ( u ) => usersById.set( String( u.id ), u ) );
                clientUsers = userRes.result.data.filter( ( u ) => this._isClient( u ) );
            }
        }
        catch ( e )
        {
            console.error( e );
            this.showMessage(
                "triangle-alert",
                _lcs( "Unterkünfte konnten nicht geladen werden" ),
                _lcs( "Bitte versuche es später erneut." )
            );
            return;
        }

        this.usersById = usersById;
        this.accommodations = accommodations;
        this.clientUsers = clientUsers;

        this.accById = new Map();
        accommodations.forEach( ( a ) => this.accById.set( String( a.id ), a ) );

        // Empty list is handled by GridJS itself (noRecordsFound message).
        this.renderGrid( accommodations );
    }

    renderGrid( accommodations )
    {
        this.stateEl.innerHTML = "";

        const rows = accommodations.map( ( a ) => [
            a.name || "—",
            this._regionLabel( a.region ),
            String( a.status || "" ),
            this._typeLabel( a.type ),
            this._ownerLabel( a ),
            String( a.id )   // actions column — id used by the formatter
        ] );

        this.grid = new Grid( {
            columns: [
                {
                    name: _lcs( "Name" ),
                    formatter: ( cell ) => html(
                        '<span class="admin-users__name" title="' + this._escape( cell ) + '">' +
                            this._escape( cell ) +
                        '</span>'
                    )
                },
                _lcs( "Region" ),
                {
                    name: _lcs( "Status" ),
                    formatter: ( cell ) => html( this._statusBadgeHtml( cell ) )
                },
                _lcs( "Typ" ),
                {
                    name: _lcs( "Eigentümer" ),
                    formatter: ( cell ) => html(
                        '<span class="admin-users__name" title="' + this._escape( cell ) + '">' +
                            this._escape( cell ) +
                        '</span>'
                    )
                },
                {
                    name: _lcs( "Aktionen" ),
                    sort: false,
                    formatter: ( cell ) => html( this._actionButtonsHtml( cell ) )
                }
            ],
            data: rows,
            search: true,
            sort: true,
            pagination: { limit: 10, summary: true },
            className: { container: "admin-users__grid" },
            language: gridLanguage( _lcs( "Unterkünften" ), _lcs( "Keine Unterkünfte gefunden" ) )
        } );

        this.grid.render( this.stateEl );
    }

    _statusBadgeHtml( status )
    {
        const key = String( status || "" ).toLowerCase();
        const label = STATUS_LABELS[ key ] ? _lcs( STATUS_LABELS[ key ] ) : ( status || "—" );
        return '<span class="acc-status acc-status--' + this._escape( key ) + '">' + this._escape( label ) + '</span>';
    }

    _actionButtonsHtml( id )
    {
        const safe = String( id ).replace( /"/g, "&quot;" );
        const ghost  = this._escape( _lcs( "Ghost-Session starten" ) );
        const detail = this._escape( _lcs( "Detail" ) );
        const edit   = this._escape( _lcs( "Bearbeiten" ) );
        const del    = this._escape( _lcs( "Löschen" ) );
        return '<div class="admin-users__actions">' +
            '<button type="button" class="admin-users__action" data-action="ghost" data-accommodation-id="' + safe + '" title="' + ghost + '" aria-label="' + ghost + '">' +
                '<i class="icon icon--ghost"></i><span>Ghost</span>' +
            '</button>' +
            '<button type="button" class="admin-users__action" data-action="detail" data-accommodation-id="' + safe + '" title="' + detail + '" aria-label="' + detail + '">' +
                '<i class="icon icon--eye"></i><span>' + detail + '</span>' +
            '</button>' +
            '<button type="button" class="admin-users__action" data-action="edit" data-accommodation-id="' + safe + '" title="' + edit + '" aria-label="' + edit + '">' +
                '<i class="icon icon--pencil"></i><span>' + edit + '</span>' +
            '</button>' +
            '<button type="button" class="admin-users__action admin-users__action--danger" data-action="delete" data-accommodation-id="' + safe + '" title="' + del + '" aria-label="' + del + '">' +
                '<i class="icon icon--trash-2"></i><span>' + del + '</span>' +
            '</button>' +
        '</div>';
    }

    /**
     * Render a centered icon + heading + text block (loading / empty / error),
     * reusing the shared .content__empty styling.
     */
    showMessage( icon, title, text )
    {
        if ( this.grid )
        {
            try { this.grid.destroy(); } catch ( e ) { /* noop */ }
            this.grid = null;
        }
        this.stateEl.innerHTML =
            '<div class="content__empty">' +
                '<span class="content__empty-icon"><i class="icon icon--' + icon + '"></i></span>' +
                '<h2></h2>' +
                '<p></p>' +
            '</div>';
        // textContent assignment avoids HTML injection from any dynamic strings.
        this.stateEl.querySelector( "h2" ).textContent = title;
        this.stateEl.querySelector( "p" ).textContent = text;
    }

    async exit()
    {
        if ( this.grid )
        {
            try { this.grid.destroy(); } catch ( e ) { /* DOM is cleared below anyway */ }
            this.grid = null;
        }
        // Base InAppState clears the page-content container.
        await super.exit();
    }

    /* ------------------------------- Helpers -------------------------------- */

    _statusLabel( status )
    {
        const key = String( status || "" ).toLowerCase();
        return STATUS_LABELS[ key ] ? _lcs( STATUS_LABELS[ key ] ) : ( status || "—" );
    }

    _typeLabel( type )
    {
        const key = String( type || "" ).toLowerCase();
        return TYPE_LABELS[ key ] ? _lcs( TYPE_LABELS[ key ] ) : ( type || "—" );
    }

    _regionLabel( region )
    {
        return REGION_LABELS[ region ] ? _lcs( REGION_LABELS[ region ] ) : ( region || "—" );
    }

    _isClient( user )
    {
        const role = user ? user.role : null;
        if ( Array.isArray( role ) )
        {
            return role.some( ( r ) => String( r ).toLowerCase() === "client" );
        }
        return String( role || "" ).toLowerCase() === "client";
    }

    // Resolve a friendly owner label from the user map; fall back to the raw id.
    _ownerLabel( accommodation )
    {
        const owner = this.usersById ? this.usersById.get( String( accommodation.ownerUserId ) ) : null;
        if ( owner )
        {
            const name = owner.data && owner.data.name ? String( owner.data.name ) : "";
            return name || owner.email || owner.organisationName || String( accommodation.ownerUserId || "—" );
        }
        return String( accommodation.ownerUserId || "—" );
    }

    _escape( str )
    {
        return String( str ).replace( /[&<>"']/g, ( c ) =>
            ( { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ c ] ) );
    }

    getContentHeaderData()
    {
        return {
            title: _lcs( "Unterkünfte" ),
            subtitle: _lcs( "Alle Unterkünfte der Plattform" )
        };
    }
}
